// calendarEventFormatter.js
// Utility CONDIVISA per titoli/venue/label degli eventi calendario.
// Fonte unica di verità: elimina la logica duplicata da Day/Week/Grid.
//
// Regole luogo (venue):
//  - scelta_fornitore / scelta_extra: luogo = FORNITORE/SHOWROOM (venue snapshot),
//    MAI la città del cliente/lead.
//  - sopralluogo / scelta_materiali: luogo = cliente/cantiere (lead property).
//  - sede / interno / personale: nessun falso indirizzo (solo tipo/room/title).

const TYPE_LABELS = {
  sopralluogo: 'Sopralluogo',
  sede: 'Appuntamento in sede',
  incontro_sede: 'Appuntamento in sede',
  presentazione: 'Presentazione',
  scelta_fornitore: 'Scelta fornitore',
  scelta_extra: 'Scelta fornitore',
  scelta_materiali: 'Scelta materiali',
  interno: 'Interno',
  personale: 'Personale',
  scadenza_contabile: 'Scadenza contabile',
  google_external: 'Google',
};

const SUPPLIER_TYPES = new Set(['scelta_fornitore', 'scelta_extra']);
const SEDE_TYPES = new Set(['sede', 'incontro_sede']);

export const getAppointmentTypeLabel = (type) =>
  TYPE_LABELS[type] || type || 'Appuntamento';

// Deriva città da indirizzo free-text ("Via Roma 12, Milano" -> "Milano").
const parseCityFromAddress = (address) => {
  if (!address || !String(address).trim()) return '';
  let seg;
  const raw = String(address).trim();
  if (raw.includes(',')) seg = raw.split(',').pop();
  else if (raw.includes(' - ')) seg = raw.split(' - ').pop();
  else seg = raw;
  return seg
    .replace(/\b\d{5}\b/g, '')
    .replace(/\([^)]*\)/g, '')
    .trim()
    .replace(/^[,\-\s]+|[,\-\s]+$/g, '');
};

const getClientName = (lead) => {
  if (!lead) return '';
  if (lead.opportunity_name) return lead.opportunity_name;
  if (lead.is_business && lead.business_name) return lead.business_name;
  return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
};

// Ritorna { name, city, address, type } | null
export const getAppointmentVenue = (apt, lead) => {
  if (!apt) return null;
  const type = apt.appointment_type;
  if (apt.source === 'google' || type === 'google_external') return null;

  // 1) VENUE SNAPSHOT (source of truth)
  if (apt.venue_name || apt.venue_city || apt.venue_address) {
    return {
      name: apt.venue_name || null,
      city: apt.venue_city || parseCityFromAddress(apt.venue_address) || null,
      address: apt.venue_address || null,
      type: apt.venue_type || 'fornitore',
    };
  }

  // 2) Tipi fornitore SENZA snapshot: NON usare la città del lead.
  if (SUPPLIER_TYPES.has(type)) {
    let name = null;
    if (apt.title && apt.title.includes(' - ')) {
      name = apt.title.split(' - ').slice(1).join(' - ').trim();
    } else {
      name = apt.title || null;
    }
    return name ? { name, city: null, address: null, type: 'fornitore' } : null;
  }

  // 3) In sede: nessun luogo fisico inventato
  if (SEDE_TYPES.has(type)) return null;

  // 4) sopralluogo / scelta_materiali / altri con lead: luogo = cliente/cantiere
  if (lead) {
    let city = lead.property_city || lead.city || '';
    if (!city) city = parseCityFromAddress(lead.property_address);
    if (!city) city = parseCityFromAddress(lead.address);
    if (city) {
      return {
        name: null,
        city,
        address: lead.property_address || lead.address || null,
        type: 'cliente',
      };
    }
  }
  return null;
};

// Titolo evento: "Tipo · [Sede] · [Città] · Cliente"
export const buildCalendarEventTitle = (apt, lead, opts = {}) => {
  const { compact = false, sep = ' · ' } = opts;
  if (!apt) return '';
  const type = apt.appointment_type;
  const typeLabel = getAppointmentTypeLabel(type);

  if (apt.source === 'google' || type === 'google_external') {
    return apt.title || 'Evento Google';
  }

  if (['personale', 'interno'].includes(type)) {
    const location = apt.room_id ? 'Ufficio' : '';
    const t = apt.title || (apt.notes ? apt.notes.substring(0, 30) : '');
    if (location && t) return `${typeLabel}${sep}${location}${sep}${t}`;
    if (t) return `${typeLabel}${sep}${t}`;
    return typeLabel;
  }

  const client = getClientName(lead);
  const venue = getAppointmentVenue(apt, lead);

  // In sede: solo tipo (+ cliente), nessun falso indirizzo
  if (SEDE_TYPES.has(type)) {
    return client ? `${typeLabel}${sep}${client}` : typeLabel;
  }

  const parts = [typeLabel];
  if (SUPPLIER_TYPES.has(type)) {
    if (venue?.name && !compact) parts.push(venue.name);
    if (venue?.city) parts.push(venue.city);
  } else if (venue?.city) {
    parts.push(venue.city);
  }
  if (client) parts.push(client);

  if (parts.length === 1) return apt.title || typeLabel;
  return parts.join(sep);
};
